import React, {useEffect, useMemo, useState} from 'react';
import {Col, Grid, Row} from "xps-react";
import {AlertMessage, Button, Dropdown, DropdownItem, Name} from '../../components';
import {clientManagementApiClient} from "../ClientManagementApiClient";
import {useHistory, useParams} from "react-router-dom";
import {NewClientPrimaryContact} from "./NewClientPrimaryContact";
import {EffectiveTaxRates, taxRateWithDefaults} from "./EffectiveTaxRates";
import {referenceDataClient} from "../../core/ReferenceDataClient";
import {ReferenceDataType} from "../../models/referenceData/ReferenceDataType";
import {EffectiveFees} from './EffectiveFees';
import {
    getSampleTypeValue,
    isNationalSampleSelection,
    validateProfileResponseContainer,
    validateProfileResponseContainerWithRequiredSampleType
} from '../models/ProfileResponse';
import {useMsal} from "@azure/msal-react";
import {msalUtils} from "../../MsalUtils";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {clearPartnerWorkstationState, selectLinkedUlek} from "../PartnerWorkstation/partnerWorkstationSlice";
import {
    NewClientProfileValidationContainer,
    selectIsSaveButtonClicked,
    selectIsValidForm,
    selectNewClientFormInteractions,
    selectNewClientFormValidation,
    selectProfileRequestForm,
    setIsSaveButtonClicked,
    setIsValidForm,
    setNewClientFormInteractions,
    setNewClientFormValidation,
    setProfileRequestForm
} from "./NewClientProfileSlice";
import NewClientFinancialModel from "./NewClientFinancialModel";
import {useAppInsights} from "../../AppInsights";
import {MeetingParticipation} from "../Meeting/Meeting";
import {validateInputRemovingSpaces} from "../../utils/stringUtils";
import {RouteWithProfileType} from "../../routes/types";
import {ServiceTeamMember} from "../models/ServiceTeam";
import {partnerApiClient} from "../PartnerApiClient";
import {ProfileSettingsContent, ServiceTeamTab} from "../ProfileSettings/ProfileSettings";
import {NO_OP} from "../../constants/common";
import {Partner} from "../models/PartnerResource";
import {checkGender} from "../models/MemberType";
import {calculateAgeFromBirthdate} from "../../utils/dateUtils";
import moment from "moment";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {selectIsNationalSampleAdmin} from "../../Authorization/nationalSampleAdminSlice";
import GenericErrorModal, {
    getGenericCommunicationErrorModalData,
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "../../components/Modal/Error/GenericErrorModal";
import type {DropdownOnChangeData} from "../../components/Dropdown/Dropdown";

type NewClientProfileProps = {
    initialReferenceData?: ReferenceDataType,
}

export const defaultReferenceData: ReferenceDataType = {
    defaultPersonalPlanningHorizon: 100,
    statesOfResidency: [],
    taxRates: {
        federalTaxRateDefault: '',
        stateTaxRateDefault: '',
        capitalGainsPercentDefault: '',
        federalTaxRateMinimum: '',
        federalTaxRateMaximum: '',
        stateTaxRateMinimum: '',
        stateTaxRateMaximum: '',
        capitalGainsPercentMinimum: '',
        capitalGainsPercentMaximum: ''
    },
    managementFees: {
        managementFeePercentDefault: '',
        managementFeePercentMaximum: '',
        managementFeePercentMinimum: ''
    },
    accreditedStatus: {
        defaultValue: '',
        items: [],
    },
    allocationType: {
        defaultValue: '',
        items: [],
    }
};

const NewClientProfile: React.FC<NewClientProfileProps> = (props) => {
    const {
        initialReferenceData = defaultReferenceData,
    } = props;

    const history = useHistory();
    const msal = useMsal();
    // TODO: Refactor reference data to its own redux slice
    const [referenceData, updateReferenceData] = useState(initialReferenceData);
    const [serviceTeamMembers, setServiceTeamMembers] = useState<ServiceTeamMember[]>([]);
    const [isFromOne, setIsFromOne] = useState(false);
    const [genericError, setGenericError] = useState<GenericErrorModalData>(genericEmptyErrorModalData);

    const dispatch = useAppDispatch();
    const linkedUlek = useAppSelector(selectLinkedUlek)!;
    const profileRequestForm = useAppSelector(selectProfileRequestForm);
    const isSaveButtonClicked = useAppSelector(selectIsSaveButtonClicked)
    const {newClientProfileForm} = useAppSelector(selectNewClientFormValidation);
    const formInteractions = useAppSelector(selectNewClientFormInteractions);
    const isValidForm = useAppSelector(selectIsValidForm);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const appInsights = useAppInsights();
    const {type} = useParams<RouteWithProfileType>();
    const releaseToggles: any = useAppSelector(selectReleaseToggles);
    const isNationalSampleAdmin = useAppSelector(selectIsNationalSampleAdmin);

    const isProspect = useMemo(() => {
        return type.toUpperCase() === 'PROSPECT';
    }, [type]);

    const isSample = useMemo(() => {
        return type.toUpperCase() === 'SAMPLE';
    }, [type]);

    const shouldRequireSampleType = useMemo(() => {
        return isSample && (releaseToggles?.enableStandardizedClientPersonas ?? false);
    }, [isSample, releaseToggles?.enableStandardizedClientPersonas]);

    const shouldShowSampleTypeDropdown = useMemo(() => {
        return shouldRequireSampleType && isNationalSampleAdmin;
    }, [isNationalSampleAdmin, shouldRequireSampleType]);

    const isNationalSampleSelected = useMemo(() => {
        return isSample && profileRequestForm.isNationalSample === true;
    }, [isSample, profileRequestForm.isNationalSample]);

    const shouldShowValidation = useMemo(() => {
        return isSaveButtonClicked || Object.values(formInteractions).some(Boolean);
    }, [formInteractions, isSaveButtonClicked]);

    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                const data: ReferenceDataType = await referenceDataClient.getReferenceData();
                updateReferenceData(data);
                let primaryContact = profileRequestForm.primaryContact;

                if (linkedUlek) {
                    try {
                        const oneDataResponse = await clientManagementApiClient.getUlekDetails(linkedUlek);

                        if (oneDataResponse && oneDataResponse.firstName) {
                            setIsFromOne(true);
                            primaryContact = {
                                ...primaryContact,
                                firstName: oneDataResponse.firstName,
                                lastName: oneDataResponse.lastName,
                                birthdate: oneDataResponse.birthDate || "",
                                gender: checkGender(oneDataResponse.gender),
                                middleInitial: oneDataResponse.middleInitial,
                                age: oneDataResponse.birthDate ? calculateAgeFromBirthdate(moment(oneDataResponse.birthDate).format('MM/DD/YYYY')) : 0
                            }
                        }
                    } catch (error) {
                        console.error('Could not fetch ULEK details', error);
                    }
                }

                dispatch(setProfileRequestForm({
                    ...profileRequestForm,
                    primaryContact: {
                        ...primaryContact,
                        personalPlanningHorizon: data.defaultPersonalPlanningHorizon
                    },
                    effectiveTaxRate: taxRateWithDefaults(profileRequestForm.effectiveTaxRate, data.taxRates),
                    managementFees: data.managementFees.managementFeePercentDefault,
                    accreditedStatus: data.accreditedStatus.defaultValue,
                    allocationType: data.allocationType.defaultValue,
                    alignmentStrategy: profileRequestForm.alignmentStrategy || "Accumulation",
                }));
            } catch (error) {
                console.error('Could not fetch reference data', error);
            }
        };

        fetchReferenceData();
    }, []);

    useEffect(() => {
        if ((isProspect || isSample) && msalUtils.getLanId(msal)) {
            partnerApiClient.getPartnerDetail(msalUtils.getLanId(msal)).then((serviceTeamMember) => {
                setServiceTeamMembers([serviceTeamMember]);
            })
        }
    }, [msal]);

    const preferredNameLabel = useMemo(() => {
        return releaseToggles?.enableOneIntegration && type.toUpperCase() === 'CLIENT'
            ? "Preferred Name"
            : "First Name";
    }, [releaseToggles?.enableOneIntegration, type]);

    useEffect(() => {
        checkFormIsValid(shouldShowValidation);
    }, [profileRequestForm, preferredNameLabel, shouldRequireSampleType, shouldShowValidation]);

    useEffect(() => {
        if (shouldRequireSampleType && !isNationalSampleAdmin && profileRequestForm.isNationalSample === undefined) {
            dispatch(setProfileRequestForm({
                ...profileRequestForm,
                isNationalSample: false
            }));
        }
    }, [dispatch, isNationalSampleAdmin, profileRequestForm, shouldRequireSampleType]);

    const handleGenericErrorClose = () => {
        setGenericError((currentError) => ({...currentError, isOpen: false}));
    };

    const checkFormIsValid = (updateValidationState: boolean = true) => {
        const profileValidation: NewClientProfileValidationContainer = shouldShowSampleTypeDropdown
            ? validateProfileResponseContainerWithRequiredSampleType(
                profileRequestForm,
                preferredNameLabel,
                true
            )
            : validateProfileResponseContainer(
                profileRequestForm,
                preferredNameLabel,
                true
            );

        if (updateValidationState) {
            dispatch(setNewClientFormValidation(profileValidation));
        }

        const isValid = Object.values(profileValidation).every(x => x === null);
        dispatch(setIsValidForm(isValid));
        return isValid;
    }

    const handleSaveButton = async () => {
        if (checkFormIsValid()) {
            if (saveButtonDisabled) {
                return;
            }
            setSaveButtonDisabled(true);

            const profileRequest = {
                ...profileRequestForm,
                prospect: isProspect,
                sample: isSample,
                resourceCode: linkedUlek
            };
            const serviceTeamRequest = {
                profile: profileRequest,
                serviceTeamMembers: isNationalSampleSelected ? [] : serviceTeamMembers
            };

            if (isProspect) {
                clientManagementApiClient.postProspect(serviceTeamRequest).then((prospectResponse) => {
                    appInsights.trackEvent({
                        name: 'ProfileSettings',
                        properties:
                            {
                                screen: 'Profile Settings',
                                action: 'Save Button Click',
                                meetingStatus: MeetingParticipation.OUT_OF_MEETING,
                                data: 'Profile Created'
                            }
                    })

                    resetLinkedClient();
                    history.push({
                        pathname: `/Profile/${prospectResponse.profile.id}/ClientProfile/FamilyTree`
                    })
                })
            } else if (isSample) {
                clientManagementApiClient.postSample(serviceTeamRequest).then((sampleResponse) => {
                    appInsights.trackEvent({
                        name: 'ProfileSettings',
                        properties:
                            {
                                screen: 'Profile Settings',
                                action: 'Save Button Click',
                                meetingStatus: MeetingParticipation.OUT_OF_MEETING,
                                data: 'Profile Created'
                            }
                    })

                    resetLinkedClient();
                    history.push({
                        pathname: `/Profile/${sampleResponse.profile.id}/ClientProfile/FamilyTree`
                    })
                }).catch((error) => {
                    console.error('Could not create sample', error);
                    setSaveButtonDisabled(false);
                    setGenericError(getGenericCommunicationErrorModalData(error));
                })
            } else {
                clientManagementApiClient.postProfile(profileRequest).then(profileResponse => {
                    appInsights.trackEvent({
                        name: 'ProfileSettings',
                        properties:
                            {
                                screen: 'Profile Settings',
                                action: 'Save Button Click',
                                meetingStatus: MeetingParticipation.OUT_OF_MEETING,
                                data: 'Profile Created'
                            }
                    })

                    resetLinkedClient();
                    history.push({
                        pathname: `/Profile/${profileResponse.id}/ClientProfile/FamilyTree`
                    })

                }).catch((error) => {
                    console.error('Could not create profile', error.message);
                    setSaveButtonDisabled(false)
                })
            }
        } else {
            dispatch(setIsSaveButtonClicked(true))
        }
    };

    const resetLinkedClient = () => {
        dispatch(clearPartnerWorkstationState());
    }

    const profileTypeFormattedText = () => {
        switch (type.toUpperCase()) {
            case ('PROSPECT'):
                return {
                    header: 'Prospect',
                }
            case ('SAMPLE'):
                return {
                    header: 'Sample',
                }
            default:
                return {
                    header: 'Client',
                }
        }
    }

    const generalSettings = <Grid>
        <Row>
            <Col>
                <div className="h4">Profile Details</div>
                <hr className="section"/>
                <div className="parent">
                    <Name name={profileRequestForm.displayName}
                          label={"Display Name"}
                          onChange={(e: any) => dispatch(setProfileRequestForm({
                              ...profileRequestForm,
                              displayName: e.target.value && validateInputRemovingSpaces(e.target.value)
                          }))}
                          required={true}
                          error={newClientProfileForm?.displayName}
                          hasInteractions={formInteractions.displayName}
                          whenUserHasInteracted={() => {
                              dispatch(setNewClientFormInteractions({
                                  ...formInteractions,
                                  displayName: true
                              }));
                              checkFormIsValid();
                          }}
                          forceShowErrors={isSaveButtonClicked}/>
                </div>
                {shouldShowSampleTypeDropdown && <div className="parent">
                    <label className="h5 label" htmlFor="sampleTypeInput" id="sampleTypeInput-label">
                        Sample Type <i className="color-text--error">*</i>
                    </label>
                    <Dropdown
                        className="sampleTypeInput"
                        aria-label="sampleTypeInput"
                        aria-labelledby="sampleTypeInput-label"
                        alignRight={false}
                        buttonIcon="right"
                        buttonKind="secondary"
                        buttonSize="medium"
                        data-testid="sampleTypeInput"
                        defaultPageSize={15}
                        defaultText="Select..."
                        dropdownKind="select"
                        dynamicDropAlign={false}
                        dynamicDropDirection={false}
                        error={formInteractions.sampleType || isSaveButtonClicked
                            ? newClientProfileForm?.sampleType ?? undefined
                            : undefined}
                        id="sampleTypeInput"
                        iconNameClose="arrow_drop_up"
                        iconNameOpen="arrow_drop_down"
                        labelWidth="300px"
                        nativeOnMobile={false}
                        onBlur={() => {
                            dispatch(setNewClientFormInteractions({
                                ...formInteractions,
                                sampleType: true
                            }));
                            checkFormIsValid();
                        }}
                        onChange={(e: DropdownOnChangeData<string>) => {
                            dispatch(setProfileRequestForm({
                                ...profileRequestForm,
                                isNationalSample: isNationalSampleSelection(e.itemText)
                            }));
                        }}
                        panelHeight="auto"
                        panelWidth="300px"
                        required={false}
                        value={getSampleTypeValue(profileRequestForm.isNationalSample)}
                        virtualScroll={false}
                    >
                        <DropdownItem itemText="National Sample" value="National Sample"/>
                        <DropdownItem itemText="My Sample" value="My Sample"/>
                    </Dropdown>
                </div>}
                <NewClientPrimaryContact
                    referenceData={referenceData}
                    isFromOne={isFromOne}
                    onValidateOnBlur={() => checkFormIsValid()}
                />
            </Col>
            <Col>
                <EffectiveTaxRates taxRate={profileRequestForm.effectiveTaxRate}
                                   updateTaxRate={(taxRate) => {
                                       dispatch(setProfileRequestForm({
                                           ...profileRequestForm,
                                           effectiveTaxRate: taxRate
                                       }))
                                   }}
                                   referenceData={referenceData}/>
                <NewClientFinancialModel referenceData={referenceData}
                                         financialModel={{
                                             accreditedStatus: profileRequestForm.accreditedStatus || referenceData.accreditedStatus.defaultValue,
                                             allocationType: profileRequestForm.allocationType || referenceData.allocationType.defaultValue,
                                             alignmentStrategy: profileRequestForm.alignmentStrategy || "Accumulation"
                                         }}
                                         updateFinancialModel={(financialModel) => {
                                             dispatch(setProfileRequestForm({
                                                 ...profileRequestForm,
                                                 accreditedStatus: financialModel.accreditedStatus,
                                                 allocationType: financialModel.allocationType,
                                                 alignmentStrategy: financialModel.alignmentStrategy
                                             }))
                                         }
                                         }
                />
                <EffectiveFees managementFees={profileRequestForm.managementFees}
                               updateManagementFees={(managementFees) => {
                                   dispatch(setProfileRequestForm({
                                       ...profileRequestForm,
                                       managementFees: managementFees
                                   }))
                               }}
                               referenceData={referenceData}/>
            </Col>
        </Row>
    </Grid>;

    const serviceTeamSettings = <ServiceTeamTab
        serviceTeam={serviceTeamMembers}
        savedProfileOwner={msalUtils.getLanId(msal)}
        profileOwnerFormState={msalUtils.getLanId(msal)}
        updateProfileOwnerFormState={NO_OP}
        userLanID={msalUtils.getLanId(msal)}
        hasLinkedUlek={false}
        disabled={false}
        isProspect
        isSample
        isProposal={false}
        onPartnerSelect={isProspect
            ? (partner: Partner) => {
                setServiceTeamMembers([...serviceTeamMembers, partner])
            }
            : NO_OP}
        onRemoveMember={isProspect
            ? (partnerCode: string) => {
                setServiceTeamMembers(serviceTeamMembers.filter((member) => member.partnerCode !== partnerCode))
            }
            : NO_OP}/>;

    return (
        <div className="new-client-profile-page">
            <div className="client-table">
                <Grid>
                    <Row>
                        <Col>
                            <span
                                className="font-xxl font-xxl">{`New ${profileTypeFormattedText().header} Profile`}</span>
                            <p className="font-default color-text--error noMargin">* Required fields</p>
                        </Col>
                        <Col>
                            <div className="textalign-right">
                                <Button className="marginright-md"
                                        icon="none"
                                        id="cancel_new_client_profile_button"
                                        includeRef={false}
                                        kind="secondary"
                                        onClick={() => {
                                            resetLinkedClient();
                                            history.push(`/Dashboard/${type}`)
                                        }}
                                        size="medium"
                                        tabIndex={0}
                                        type="button"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    icon="none"
                                    id="save_new_client_profile_button"
                                    includeRef={false}
                                    kind="primary"
                                    onClick={handleSaveButton}
                                    size="medium"
                                    tabIndex={0}
                                    type="button"
                                    disabled={saveButtonDisabled}
                                >
                                    SAVE
                                </Button>
                            </div>
                        </Col>
                        {!isValidForm && isSaveButtonClicked && (
                            <AlertMessage
                                className="display-flex justify-content-right alertBanner"
                                id="alertBanner"
                                fullWidth={false}
                                icon="warning"
                                showAlert={true}
                                showCloseBtn={false}
                                type="error"
                            >
                                <b className="alertBannerStyleName">Required fields missing</b>
                                &nbsp; &nbsp;
                                <b className="font-weight-400">All required fields must be entered to save a client
                                    profile.</b>
                            </AlertMessage>
                        )}
                    </Row>
                </Grid>
                <ProfileSettingsContent
                    showServiceTeamTab={isProspect || (isSample && !isNationalSampleSelected)}
                    generalSettings={generalSettings}
                    serviceTeamSettings={serviceTeamSettings}/>
                <GenericErrorModal
                    errorModalData={genericError}
                    onClickButton={handleGenericErrorClose}
                    onRequestClose={handleGenericErrorClose}
                />
            </div>
        </div>
    );
};

export default NewClientProfile;
